import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Network, RefreshCw, AlertCircle, ChevronDown } from "lucide-react";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { api as axios } from "../lib/api";

const API = "";

const STATUS_COLORS = {
  PLANNED:   "bg-blue-50 text-blue-700 border-blue-200",
  OPEN:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  CLOSED:    "bg-slate-100 text-slate-600 border-slate-200",
  CANCELLED: "bg-red-50 text-red-600 border-red-200",
};

const STATUS_TRANSITIONS = {
  PLANNED: ["OPEN", "CANCELLED"],
  OPEN:    ["CLOSED"],
};

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}
    >
      {status}
    </span>
  );
}

export default function EnrollmentOfferings() {
  const { user } = useAuth();
  const [offerings, setOfferings]         = useState([]);
  const [refData, setRefData]             = useState(null);
  const [loading, setLoading]             = useState(true);
  const [showCreate, setShowCreate]       = useState(false);
  const [creating, setCreating]           = useState(false);
  const [transitioning, setTransitioning] = useState(null);

  // Form state
  const [selectedProgram,  setSelectedProgram]  = useState("");
  const [selectedVersion,  setSelectedVersion]  = useState("");
  const [selectedYear,     setSelectedYear]      = useState("");
  const [deliveryMode,     setDeliveryMode]      = useState("ONSITE");
  const [capacity,         setCapacity]          = useState("");

  const canManage = ["registrar", "institution_admin", "super_admin"].includes(user?.role);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [offerRes, refRes] = await Promise.all([
        axios.get(`/enrollment/offerings`),
        axios.get(`/enrollment/reference-data`),
      ]);
      setOfferings(offerRes.data.items || []);
      setRefData(refRes.data);
    } catch (err) {
      toast.error("Failed to load program offerings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const availableVersions = refData?.programs?.find(p => p.id === selectedProgram)?.versions || [];

  const handleCreate = async () => {
    if (!selectedVersion || !selectedYear) {
      toast.error("Select a program version and academic year.");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`/enrollment/offerings`, {
        program_version_id: selectedVersion,
        academic_year_id:   selectedYear,
        delivery_mode:      deliveryMode,
        capacity:           capacity ? parseInt(capacity, 10) : null,
      });
      toast.success("Program offering created (PLANNED).");
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create offering.");
    } finally {
      setCreating(false);
    }
  };

  const handleTransition = async (offeringId, newStatus) => {
    setTransitioning(offeringId);
    try {
      await axios.patch(`/enrollment/offerings/${offeringId}/status`, {
        status: newStatus,
      });
      toast.success(`Offering updated to ${newStatus}.`);
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Transition failed.");
    } finally {
      setTransitioning(null);
    }
  };

  const resetForm = () => {
    setSelectedProgram("");
    setSelectedVersion("");
    setSelectedYear("");
    setDeliveryMode("ONSITE");
    setCapacity("");
  };

  return (
    <>
      <PageHeader
        eyebrow="Student Lifecycle / Academics"
        title="Program Offerings"
        description="Manage program offerings that authorize enrollment. Each offering links a Program Version to an Academic Year."
        actions={
          canManage && (
            <Button
              data-testid="create-offering-btn"
              onClick={() => setShowCreate(true)}
              size="sm"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Create Offering
            </Button>
          )
        }
      />
      <ResponsivePageContainer>
        {loading ? (
          <div className="flex items-center gap-2 py-16 text-muted-foreground justify-center">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading program offerings…
          </div>
        ) : offerings.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center"
            data-testid="no-offerings-state"
          >
            <Network className="mb-3 h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">No program offerings yet</p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">
              Create an offering to link a Program Version to an Academic Year before
              uploading enrollment CSVs.
            </p>
            {canManage && (
              <Button
                className="mt-4"
                size="sm"
                onClick={() => setShowCreate(true)}
                data-testid="create-offering-empty-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Create Offering
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <div className="table-scroll-container">
              <table className="w-full text-sm" data-testid="offerings-table">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Program</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Version</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Academic Year</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Delivery</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Capacity</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Enrollments</th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                    {canManage && (
                      <th className="px-4 py-3 text-left font-medium text-muted-foreground">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {offerings.map(o => (
                    <tr key={o.id} className="hover:bg-muted/20 transition-colors" data-testid={`offering-row-${o.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{o.program_code}</div>
                        <div className="text-xs text-muted-foreground">{o.program_title}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{o.version_label}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">{o.academic_year_code}</td>
                      <td className="px-4 py-3 text-muted-foreground">{o.delivery_mode}</td>
                      <td className="px-4 py-3 text-muted-foreground">{o.capacity ?? "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant="secondary">{o.enrollment_count}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={o.status} />
                      </td>
                      {canManage && (
                        <td className="px-4 py-3">
                          {STATUS_TRANSITIONS[o.status]?.length > 0 ? (
                            <div className="flex gap-1.5">
                              {STATUS_TRANSITIONS[o.status].map(ns => (
                                <button
                                  key={ns}
                                  data-testid={`transition-${o.id}-${ns}`}
                                  onClick={() => handleTransition(o.id, ns)}
                                  disabled={transitioning === o.id}
                                  className={`rounded px-2 py-1 text-xs font-medium border transition-colors disabled:opacity-50 ${
                                    ns === "CANCELLED"
                                      ? "border-red-200 text-red-600 hover:bg-red-50"
                                      : "border-border text-muted-foreground hover:bg-muted"
                                  }`}
                                >
                                  {transitioning === o.id ? "…" : `→ ${ns}`}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">terminal</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ResponsivePageContainer>

      {/* Create Offering Dialog */}
      <Dialog open={showCreate} onOpenChange={open => { setShowCreate(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md" data-testid="create-offering-dialog">
          <DialogHeader>
            <DialogTitle>Create Program Offering</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Program */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Program *</label>
              <Select
                value={selectedProgram}
                onValueChange={v => { setSelectedProgram(v); setSelectedVersion(""); }}
              >
                <SelectTrigger data-testid="select-program">
                  <SelectValue placeholder="Select a program" />
                </SelectTrigger>
                <SelectContent>
                  {refData?.programs?.map(p => (
                    <SelectItem key={p.id} value={p.id} data-testid={`prog-option-${p.code}`}>
                      {p.code} — {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Program Version */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Program Version *</label>
              <Select
                value={selectedVersion}
                onValueChange={setSelectedVersion}
                disabled={!selectedProgram}
              >
                <SelectTrigger data-testid="select-version">
                  <SelectValue placeholder={selectedProgram ? "Select a version" : "Select a program first"} />
                </SelectTrigger>
                <SelectContent>
                  {availableVersions.map(v => (
                    <SelectItem key={v.id} value={v.id} data-testid={`ver-option-${v.id}`}>
                      {v.label} — {v.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Academic Year */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Academic Year *</label>
              {refData?.academic_years?.length === 0 ? (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  No academic years configured for this institution. Configure the academic calendar first.
                </div>
              ) : (
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger data-testid="select-year">
                    <SelectValue placeholder="Select an academic year" />
                  </SelectTrigger>
                  <SelectContent>
                    {refData?.academic_years?.map(ay => (
                      <SelectItem key={ay.id} value={ay.id} data-testid={`ay-option-${ay.code}`}>
                        {ay.label} ({ay.start_date?.substring(0, 4)}–{ay.end_date?.substring(0, 4)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Delivery Mode */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Delivery Mode *</label>
              <Select value={deliveryMode} onValueChange={setDeliveryMode}>
                <SelectTrigger data-testid="select-delivery-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(refData?.delivery_modes || ["ONSITE", "ONLINE", "HYBRID"]).map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Capacity */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Capacity (optional)</label>
              <input
                type="number"
                min="1"
                value={capacity}
                onChange={e => setCapacity(e.target.value)}
                placeholder="Leave blank for unlimited"
                data-testid="input-capacity"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !selectedVersion || !selectedYear}
              data-testid="confirm-create-offering-btn"
            >
              {creating ? "Creating…" : "Create Offering"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
